
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { t } from 'i18next';

import { styled, Box, Divider } from '@mui/material';

import {
  Label,
  Button,
  KeyIcon,
  Typography,
  FormEmailField,
  FormTextField,
  FormPasswordField,
  Link,
} from '@datanomik/commons-ui';

import {
  ArrowLeftSLine,
  Datanomik,
} from '@datanomik/commons-ui/icons';

import { FormProvider } from 'react-hook-form';

import { clearHeaders } from '@/interceptors/Api';
import { isDemo } from '@/config/apiUrls';
import { DemoIntroPage } from '@/features/demo/DemoIntroPage';
import Grid from '../../common/Grid';
import Sticky from '../../common/Sticky';
import { SwiperRaw, SwiperSlide } from '../../common/Swiper';
import { swiperStyles } from '../../common/Swiper/styles';
import { DatanomikIsoBlue } from '../../common/Logo';

import ForgotPassword from '../ForgotPassword';
import ContentBoxOld from './ContentBox';
import ContentBoxNew from '@/features/login/ContentBoxNew';
import { loginConfig } from '@/features/login/loginConfig';

import useLogIn from './hooks';
import styles from './styles';

const ContentBox = loginConfig.useNewContentBox ? ContentBoxNew : ContentBoxOld;

const Swiper = styled(SwiperRaw)(swiperStyles);

const TRANSITION_MS = 600;

const LogInRaw = ({ className }) => {
  const isDemoMode = isDemo();
  const [splashVisible, setSplashVisible] = useState(isDemoMode);
  const [splashMounted, setSplashMounted] = useState(isDemoMode);
  const [loginReady, setLoginReady] = useState(false);
  const {
    swiperRef,
    formMethodsOpenId,
    formMethodsDatanomik,
    openForgotPassword,
    handleOnSubmitOpenId,
    handleOnSubmitDatanomik,
    handleOpenForgotPassword,
    handleCloseForgotPassword,
  } = useLogIn();

  const splashTimersRef = useRef([]);

  const year = (new Date()).getFullYear();

  // After logout, axios Instance was keeping headers, including Workspace ID
  // which was causing issues when login in with an user from another workspace.
  useEffect(() => {
    clearHeaders();
  }, []);

  // Animate login in on mount (non-demo) or after splash dismiss (demo)
  useEffect(() => {
    if (!isDemoMode) {
      requestAnimationFrame(() => setLoginReady(true));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup splash timers on unmount
  useEffect(() => () => splashTimersRef.current.forEach(clearTimeout), []);

  const handleDismissSplash = () => {
    setSplashVisible(false);
    // Small delay so the login zoom-in starts slightly after the splash begins fading
    splashTimersRef.current.push(setTimeout(() => setLoginReady(true), 100));
    splashTimersRef.current.push(setTimeout(() => setSplashMounted(false), TRANSITION_MS));
  };

  return (
    <Box className={`${className}`} sx={{ position: 'relative' }}>
      {/* Splash overlay — zooms out and fades during transition */}
      {splashMounted && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            opacity: splashVisible ? 1 : 0,
            transform: splashVisible ? 'scale(1)' : 'scale(1.08)',
            transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
            pointerEvents: splashVisible ? 'auto' : 'none',
          }}
        >
          <DemoIntroPage onContinue={handleDismissSplash} />
        </Box>
      )}

      {/* Login content — zooms in from slightly small */}
      <Box
        sx={{
          opacity: loginReady ? 1 : 0,
          transform: loginReady ? 'scale(1)' : 'scale(0.96)',
          transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
        }}
      >
        <Box className="dn-container">
          <Grid container>
            <Grid className="dn-show-mobile" item xs={12}>
              <DatanomikIsoBlue className="dn-logo-mobile dn-show-mobile" />
            </Grid>
            <Grid className="dn-hidden-mobile" item xs={12} md={6} lg="auto">
              <Sticky position="top" rootSelector="body">
                <ContentBox />
              </Sticky>
            </Grid>
            <Grid item xs={12} md className="dn-flex-center">
              <Box width="100%" maxWidth="374px" m="auto 0">
                <Grid container>
                  <Grid item xs={12} textAlign="center">
                    <KeyIcon size="xlarge" color="neutral">
                      <Datanomik />
                    </KeyIcon>
                  </Grid>
                  <Grid item xs={12} textAlign="center" mb="14px">
                    <Typography variant="h5">
                      {t('Log in to your account')}
                    </Typography>
                    <Typography variant="p3" color="text.500" mt="4px">
                      {t('Enter your details to login.')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Swiper
                      ReactSwiperProps={{
                        onSwiper: (swiper) => {
                          swiperRef.current = swiper;
                        },
                        initialSlide: 0,
                        allowTouchMove: false,
                        draggable: false,
                        autoHeight: true,
                        spaceBetween: 12,
                        grabCursor: false,
                        navigation: false,
                        keyboard: {
                          enabled: false,
                        },
                        pagination: {
                          dynamicBullets: false,
                        },
                      }}
                    >
                      <SwiperSlide key="default">
                        <Box className="dn-swiper-content">
                          <FormProvider {...formMethodsDatanomik}>
                            <form
                              id="login-form"
                              data-form-type="login"
                              autoComplete="on"
                              onSubmit={formMethodsDatanomik.handleSubmit(handleOnSubmitDatanomik)}
                              noValidate
                            >
                              <Grid container>
                                <Grid item xs={12}>
                                  <FormEmailField
                                    name="username"
                                    control={formMethodsDatanomik.control}
                                    label={<Label text={t('EMAIL')} />}
                                    labelText={t('EMAIL')}
                                    autoFocus
                                    required={!(openForgotPassword)}
                                    localeText={{
                                      isRequired: t('IS REQUIRED'),
                                      validEmailAddredd: t('Please enter a valid email address'),
                                    }}
                                    slotProps={{
                                      htmlInput: {
                                        id: 'username',
                                        autoComplete: 'username',
                                        'data-lpignore': 'false',
                                        'data-1p-ignore': 'false',
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <FormPasswordField
                                    name="password"
                                    control={formMethodsDatanomik.control}
                                    label={<Label text={t('Password')} />}
                                    labelText={t('Password')}
                                    required={!(openForgotPassword)}
                                    localeText={{
                                      isRequired: t('IS REQUIRED'),
                                    }}
                                    slotProps={{
                                      htmlInput: {
                                        id: 'password',
                                        autoComplete: 'current-password',
                                        'data-lpignore': 'false',
                                        'data-1p-ignore': 'false',
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Grid container>
                                    <Grid item xs={12} textAlign="right">
                                      <Link onClick={handleOpenForgotPassword}>
                                        {t('Forgot your password?')}
                                      </Link>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                      >
                                        {t('Login')}
                                      </Button>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Divider className="dn-divider-buttons" textAlign="center">
                                        <Typography>{t('Or')}</Typography>
                                      </Divider>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Button
                                        fullWidth
                                        type="button"
                                        variant="outlined"
                                        color="black"
                                        onClick={() => {
                                          swiperRef.current?.slideTo(1, 500);
                                        }}
                                      >
                                        {t('Login to an external account')}
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </form>
                          </FormProvider>
                        </Box>
                      </SwiperSlide>
                      <SwiperSlide key="external">
                        <Box className="dn-swiper-content">
                          <FormProvider {...formMethodsOpenId}>
                            <form
                              onSubmit={formMethodsOpenId.handleSubmit(handleOnSubmitOpenId)}
                              noValidate
                            >
                              <Grid container>
                                <Grid item xs={12}>
                                  <Grid container>
                                    <Grid item xs>
                                      <FormTextField
                                        name="loginDomain"
                                        control={formMethodsOpenId.control}
                                        labelText={t('Company name')}
                                        placeholder={t('Company name')}
                                        required
                                        localeText={{
                                          isRequired: t('IS REQUIRED'),
                                        }}
                                        slotProps={{
                                          htmlInput: {
                                            autoComplete: 'off',
                                          },
                                        }}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      width="120px"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography sx={{ fontWeight: 500 }}>
                                        {t('.datanomik.com')}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <Grid container>
                                    <Grid item xs={12}>
                                      <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                      >
                                        {t('Continue')}
                                      </Button>
                                    </Grid>
                                    <Grid item xs={12} textAlign="left">
                                      <Link
                                        onClick={() => {
                                          swiperRef.current?.slideTo(0, 500);
                                        }}
                                      >
                                        <ArrowLeftSLine />
                                        {t('Back')}
                                      </Link>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </form>
                          </FormProvider>
                        </Box>
                      </SwiperSlide>
                    </Swiper>
                  </Grid>

                </Grid>
              </Box>
            </Grid>
            <Grid className="dn-show-mobile" item xs={12}>
              <Typography className="dn-legal-mobile" variant="body2">{t('© {{year}} Datanomik. All rights reserved.', { year })}</Typography>
            </Grid>
          </Grid>
        </Box>
        {
          openForgotPassword && (
            <ForgotPassword
              openModal={openForgotPassword}
              closeModal={handleCloseForgotPassword}
            />
          )
        }
      </Box>
    </Box>
  );
};

LogInRaw.propTypes = {
  className: PropTypes.string,
};

export const LogIn = styled(LogInRaw)(styles);

LogIn.propTypes = LogInRaw.propTypes;
